/**
 * Activity Addition Results Controller (EPIC-027 / 27.5)
 *
 * Right column of /simulateur-ajout-activite.html (§17.B). Renders the delta
 * payload from POST /simulator/add-activity/calculate:
 *  - 4 before/after KPIs (URSSAF / IR / revenu net / mois bascule TVA).
 *  - Ventilation table by fiscal category, with "Nouveau" pill on categories
 *    newly introduced by the simulated activity.
 *  - Stacked threshold-alert banners (nm-banner--warning, verbatim D2 27.1).
 *  - Optional ACRE additional impact card.
 *  - CFE before/after/delta card.
 *
 * All rendering uses <template> + cloneTemplate + fillSlots — no HTML string
 * construction (project rule).
 */

import BaseController from "./base_controller.js";

const REGIME_LABELS = {
  bic_vente: "BIC vente",
  bic_services: "BIC services",
  bnc: "BNC",
};

const ALERT_TITLES = {
  vat_threshold_services: "Franchissement seuil franchise TVA",
  vat_threshold_vente: "Franchissement seuil franchise TVA",
  vat_threshold_mixed: "Franchissement seuil franchise TVA",
  vat_threshold: "Franchissement seuil franchise TVA",
  mixed_regime: "Bascule mono → mixte BIC + BNC",
  micro_exit: "Dépassement du seuil de sortie du régime micro",
};

export default class ActivityAdditionResultsController extends BaseController {
  static targets = [
    "resultsColumn",
    "kpisGrid",
    "ventilationCard",
    "ventilationBody",
    "alertsContainer",
    "acreImpactCard",
    "cfeCard",
    "ctasCard",
  ];

  connect() {
    this.lastPayload = null;
  }

  getLastPayload() {
    return this.lastPayload;
  }

  render(payload, _formState) {
    if (!payload) return;
    this.lastPayload = payload;

    this.renderKpis(payload.delta || {});
    this.renderVentilation(payload.ventilationByCategory || []);
    this.renderAlerts(payload.alerts || []);
    this.renderAcreImpact(payload.acreAdditionalImpact);
    this.renderCfe(payload.delta || {});
  }

  // ---------------------------------------------------------------------------
  // KPIs (avant / après)
  // ---------------------------------------------------------------------------

  renderKpis(delta) {
    if (!this.hasKpisGridTarget) return;
    this.kpisGridTarget.replaceChildren();

    const cards = [
      {
        label: "URSSAF · 12 mois",
        before: delta.urssafBeforeEur,
        after: delta.urssafAfterEur,
        deltaEur: delta.urssafDeltaEur,
        deltaPct: delta.urssafDeltaPercentRatio,
        worseIfHigher: true,
      },
      {
        label: "IR estimé · 12 mois",
        before: delta.incomeTaxBeforeEur,
        after: delta.incomeTaxAfterEur,
        deltaEur: delta.incomeTaxDeltaEur,
        deltaPct: delta.incomeTaxDeltaPercentRatio,
        worseIfHigher: true,
      },
      {
        label: "Revenu net · 12 mois",
        before: delta.netIncomeBeforeEur,
        after: delta.netIncomeAfterEur,
        deltaEur: delta.netIncomeDeltaEur,
        deltaPct: delta.netIncomeDeltaPercentRatio,
        worseIfHigher: false,
      },
      {
        label: "Mois de bascule TVA",
        before: delta.vatBreachMonthBefore,
        after: delta.vatBreachMonthAfter,
        isMonth: true,
      },
    ];

    cards.forEach((kpi) => {
      const fragment = this.cloneTemplate("kpi-card-tpl");
      const afterStr = kpi.isMonth
        ? formatMonth(kpi.after)
        : formatEuro(kpi.after);
      const beforeStr = kpi.isMonth ? formatMonth(kpi.before) : formatEuro(kpi.before);

      this.fillSlots(fragment, {
        label: kpi.label,
        afterValue: afterStr,
        beforeValue: beforeStr,
        deltaText: kpi.isMonth ? "" : formatDelta(kpi.deltaEur, kpi.deltaPct, kpi.worseIfHigher),
      });

      if (!kpi.isMonth) {
        const deltaEl = fragment.querySelector('[data-slot="deltaText"]');
        if (deltaEl) {
          deltaEl.classList.add(deltaClass(kpi.deltaEur, kpi.worseIfHigher));
        }
      }

      this.kpisGridTarget.append(fragment);
    });
  }

  // ---------------------------------------------------------------------------
  // Ventilation table
  // ---------------------------------------------------------------------------

  renderVentilation(rows) {
    if (!this.hasVentilationBodyTarget) return;
    this.ventilationBodyTarget.replaceChildren();

    let totalCa = 0;
    let totalUrssaf = 0;
    let totalIr = 0;

    rows.forEach((row) => {
      const fragment = this.cloneTemplate("ventilation-row-tpl");
      const isNew = (row.revenueBeforeEur || 0) === 0 && (row.revenueAfterEur || 0) > 0;

      this.fillSlots(fragment, {
        regimeLabel: REGIME_LABELS[row.category] || row.category,
        description: "",
        ca: formatEuro(row.revenueAfterEur),
        urssaf: formatEuro(row.urssafAfterEur),
        ir: formatEuro(row.incomeTaxAfterEur),
      });

      const newBadge = fragment.querySelector('[data-slot="newBadgeWrap"]');
      if (newBadge && isNew) newBadge.hidden = false;

      totalCa += Number(row.revenueAfterEur || 0);
      totalUrssaf += Number(row.urssafAfterEur || 0);
      totalIr += Number(row.incomeTaxAfterEur || 0);

      this.ventilationBodyTarget.append(fragment);
    });

    if (rows.length > 0) {
      const totalFragment = this.cloneTemplate("ventilation-total-tpl");
      this.fillSlots(totalFragment, {
        ca: formatEuro(totalCa),
        urssaf: formatEuro(totalUrssaf),
        ir: formatEuro(totalIr),
      });
      this.ventilationBodyTarget.append(totalFragment);
    }
  }

  // ---------------------------------------------------------------------------
  // Alerts (warning banners)
  // ---------------------------------------------------------------------------

  renderAlerts(alerts) {
    if (!this.hasAlertsContainerTarget) return;
    this.alertsContainerTarget.replaceChildren();
    if (alerts.length === 0) {
      this.alertsContainerTarget.hidden = true;
      return;
    }
    this.alertsContainerTarget.hidden = false;

    alerts.forEach((alert) => {
      const fragment = this.cloneTemplate("alert-banner-tpl");
      this.fillSlots(fragment, {
        title: ALERT_TITLES[alert.type] || "Information",
        message: alert.message || "",
      });
      this.alertsContainerTarget.append(fragment);
    });
  }

  // ---------------------------------------------------------------------------
  // ACRE additional impact
  // ---------------------------------------------------------------------------

  renderAcreImpact(impact) {
    if (!this.hasAcreImpactCardTarget) return;
    if (!impact) {
      this.acreImpactCardTarget.hidden = true;
      return;
    }
    this.acreImpactCardTarget.hidden = false;

    const headerValue = this.acreImpactCardTarget.querySelector("header strong");
    if (headerValue) {
      headerValue.textContent = `− ${formatEuro(impact.savingEur)}`;
    }

    const acreEndDateSlot = this.acreImpactCardTarget.querySelector('[data-slot="acreEndDate"]');
    if (acreEndDateSlot && impact.acreEndDate) {
      acreEndDateSlot.textContent = formatFrDate(impact.acreEndDate);
    }

    const degressivityBanner = this.acreImpactCardTarget.querySelector(".nm-banner.nm-banner--info");
    if (degressivityBanner) {
      degressivityBanner.hidden = !impact.degressivityApplied;
    }
  }

  // ---------------------------------------------------------------------------
  // CFE card
  // ---------------------------------------------------------------------------

  renderCfe(delta) {
    if (!this.hasCfeCardTarget) return;

    const labelBefore = this.cfeCardTarget.querySelector('[data-slot="cfeLabelBefore"]');
    const detailBefore = this.cfeCardTarget.querySelector('[data-slot="cfeDetailBefore"]');
    if (labelBefore) labelBefore.textContent = "Avant l'ajout";
    if (detailBefore) detailBefore.textContent = `${formatEuro(delta.cfeBeforeEur)} estimé · base CFE actuelle`;

    const deltaSign = (delta.cfeDeltaEur || 0) >= 0 ? "+" : "−";
    const deltaAbs = Math.abs(delta.cfeDeltaEur || 0);
    const labelAfter = this.cfeCardTarget.querySelector('[data-slot="cfeLabelAfter"]');
    const detailAfter = this.cfeCardTarget.querySelector('[data-slot="cfeDetailAfter"]');
    if (labelAfter) labelAfter.textContent = "Après l'ajout";
    if (detailAfter) detailAfter.textContent = `${formatEuro(delta.cfeAfterEur)} estimés · delta ${deltaSign} ${formatEuro(deltaAbs)} sur la base CFE projetée`;
  }
}

function formatEuro(amount) {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
}

function formatMonth(value) {
  if (!value) return "—";
  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})/);
  if (!match) return str;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

function formatFrDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
}

function formatDelta(eur, pct, worseIfHigher) {
  if (eur === null || eur === undefined) return "—";
  const n = Number(eur);
  if (Number.isNaN(n) || n === 0) return "± 0 €";
  const sign = n > 0 ? "+" : "−";
  const abs = Math.abs(n);
  const pctStr =
    pct !== null && pct !== undefined && !Number.isNaN(Number(pct))
      ? ` (${n > 0 ? "+" : "−"} ${Math.abs(Number(pct) * 100).toFixed(1)} %)`
      : "";
  return `${sign} ${abs.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €${pctStr}`;
}

function deltaClass(eur, worseIfHigher) {
  const n = Number(eur);
  if (!Number.isFinite(n) || n === 0) return "text-subtle";
  const isWorse = worseIfHigher ? n > 0 : n < 0;
  return isWorse ? "text-error" : "text-success";
}
